// Environment configuration - NEVER use localhost in production
const getApiBaseUrl = () => {
  // Always require API_BASE_URL in production
  if (process.env.NODE_ENV === 'production') {
    if (!process.env.REACT_APP_API_BASE_URL) {
      throw new Error('REACT_APP_API_BASE_URL environment variable is required in production');
    }
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Development fallback - easily switch between localhost and production
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  // return process.env.REACT_APP_API_BASE_URL || 'https://api.topclip.ai';
};

const API_BASE_URL = getApiBaseUrl();
const APP_ENV = process.env.NODE_ENV || 'development';

// Supabase configuration - required in production
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://dprrediedousriicteuz.supabase.co";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRwcnJlZGllZG91c3JpaWN0ZXV6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ4NTc2NjgsImV4cCI6MjA3MDQzMzY2OH0.f7jcQHCrHd7MtdhQPB9RSwSAcTFd8pUgnsKrU4Mp6Mc";

// PostHog configuration - required for analytics
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || '';

export const config = {
  API_BASE_URL,
  APP_ENV,
  SUPABASE_URL: SUPABASE_URL || '',
  SUPABASE_ANON_KEY: SUPABASE_ANON_KEY || '',
  POSTHOG_KEY,
  POSTHOG_HOST,
  
  isDevelopment: () => APP_ENV === 'development',
  isProduction: () => APP_ENV === 'production',
  
  endpoints: {
    processVideo: `${API_BASE_URL}/api/process-video`,
    modalProcessVideo: `${API_BASE_URL}/api/modal/process`,
    aiEnhancedProcessVideo: `${API_BASE_URL}/api/ai-enhanced-create-clips`,
    jobStatus: (id: string) => `${API_BASE_URL}/api/job-status/${id}`,
    jobStatusStream: (id: string) => `${API_BASE_URL}/api/job-status-stream/${id}`,
    cancelJob: (id: string) => `${API_BASE_URL}/api/cancel-job/${id}`,
    videoPreview: `${API_BASE_URL}/video-preview`,
    videoInfo: `${API_BASE_URL}/video-info`,
    download: (filename: string) => `${API_BASE_URL}/download/${filename}`,
    
    // Media files
    musicFiles: `${API_BASE_URL}/api/music-files`,
    gameVideos: `${API_BASE_URL}/api/game-videos`,
    musicFile: (filename: string) => `${API_BASE_URL}/music/${filename}`,
    gameVideo: (filename: string) => `${API_BASE_URL}/game_videos/${filename}`,
    
    // User clips
    userClips: (userId: string) => `${API_BASE_URL}/api/clips/${userId}`,
    clipsByJob: (jobId: string) => `${API_BASE_URL}/api/clips/by-job/${jobId}`,
    deleteClip: (userId: string, clipId: string) => `${API_BASE_URL}/api/clips/${userId}/${clipId}`,
    
    // Usage tracking
    usage: (userId: string, plan: string) => `${API_BASE_URL}/api/usage/${userId}?plan=${plan}`,
    maxClips: (userId: string) => `${API_BASE_URL}/api/usage/${userId}/max-clips`,
    recordUsage: (userId: string) => `${API_BASE_URL}/api/usage/${userId}/record`,
    refreshCache: (userId: string) => `${API_BASE_URL}/api/usage/${userId}/refresh-cache`,
    
    // Stripe - Use local API in development, production API in production
    stripe: {
      createCheckout: `${API_BASE_URL}/api/stripe/create-checkout-session`,
      upgradeSubscription: `${API_BASE_URL}/api/stripe/upgrade-subscription`,
      replaceSubscription: `${API_BASE_URL}/api/stripe/replace-subscription`,
      getSubscription: (userId: string) => `${API_BASE_URL}/api/stripe/subscription/${userId}`,
      createPortal: `${API_BASE_URL}/api/stripe/create-portal-session`,
      webhook: `${API_BASE_URL}/api/stripe/webhook`,
    }
  }
};

// Console logging for development and production validation
if (config.isDevelopment()) {
  console.log('🔧 Development Mode Config:', {
    API_BASE_URL: config.API_BASE_URL,
    APP_ENV: config.APP_ENV,
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
    SUPABASE_URL: config.SUPABASE_URL ? 'Set' : 'Missing',
    SUPABASE_ANON_KEY: config.SUPABASE_ANON_KEY ? 'Set' : 'Missing',
    REACT_APP_POSTHOG_KEY: POSTHOG_KEY ? 'Set' : 'Missing',
    REACT_APP_POSTHOG_HOST: POSTHOG_HOST ? 'Set' : 'Missing'
  });
} else if (config.isProduction()) {
  // Minimal production logging - only critical errors
  const requiredVars = [
    { name: 'REACT_APP_API_BASE_URL', value: config.API_BASE_URL },
    { name: 'REACT_APP_SUPABASE_URL', value: config.SUPABASE_URL },
    { name: 'REACT_APP_SUPABASE_ANON_KEY', value: config.SUPABASE_ANON_KEY },
    { name: 'REACT_APP_POSTHOG_KEY', value: config.POSTHOG_KEY },
    { name: 'REACT_APP_POSTHOG_HOST', value: config.POSTHOG_HOST }
  ];
  
  const missingVars = requiredVars.filter(v => !v.value);
  if (missingVars.length > 0) {
    console.error('❌ Missing required environment variables:', missingVars.map(v => v.name));
  }
  
  // Critical check for production API configuration
  if (config.API_BASE_URL.includes('localhost')) {
    console.error('⚠️ PRODUCTION ERROR: API_BASE_URL is pointing to localhost!');
    console.error(`Current API_BASE_URL: ${config.API_BASE_URL}`);
  }
}